import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  ViewContainerRef,
  type ComponentRef,
} from '@angular/core';
import {FormArray, FormGroup, FormGroupDirective, NgControl} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {ControlErrorComponent} from '@shared/components/control-error/control-error.component';
import {FORM_ERRORS} from '../services/error-handler';
import {FormValidationHelperService} from '@shared/services/form-validation-helper.service';
import {placeFormErrorElement, setFormControlInvalidClass} from './place-form-error.util';

@UntilDestroy()
@Directive({
  selector: '[appFormError]',
  standalone: true,
})
export class FormErrorDirective implements OnChanges {
  text!: string;
  compRef!: ComponentRef<ControlErrorComponent> | undefined;
  @Input() autoGeneratedError: boolean = true;
  @Input() appendedInput: boolean = false;
  @Input() needSpace: boolean = true;
  @Output() errorStatusChanged: EventEmitter<string | null> = new EventEmitter<string | null>();
  @Input() moreCheckRequired: boolean = false;
  @Input() validationAttempted = false;

  private syncErrorState?: () => void;

  constructor(
    @Self() private control: NgControl,
    @Inject(FORM_ERRORS) private errors: any,
    private elementRef: ElementRef,
    private vcr: ViewContainerRef,
    @Optional() private formGroupDirective: FormGroupDirective | null,
    private formValidationHelper: FormValidationHelperService,
  ) {}

  removeError() {
    if (!this.compRef) {
      return;
    }

    const hostEl = this.compRef.location?.nativeElement as HTMLElement | undefined;
    if (hostEl?.parentNode) {
      hostEl.parentNode.removeChild(hostEl);
    }
    this.compRef.destroy();
    this.compRef = undefined;
  }

  setError(text: string) {
    if (!this.autoGeneratedError) {
      return;
    }

    if (!this.compRef) {
      this.compRef = this.vcr.createComponent(ControlErrorComponent);
    }

    this.compRef.instance.needSpace = this.needSpace;
    this.compRef.instance.setError(text);
    this.compRef.changeDetectorRef.detectChanges();
    this.placeErrorOutsideInputGroup();
  }

  setErrorStyleForInput() {
    setFormControlInvalidClass(this.elementRef.nativeElement as HTMLElement, true);
  }

  removeErrorStyleForInput() {
    setFormControlInvalidClass(this.elementRef.nativeElement as HTMLElement, false);
  }

  private destroyed = false;
  private errorStateQueued = false;
  private showErrorsAfterValidationAttempt = false;

  ngOnInit(): void {
    // Defer past FormGroupDirective.resetForm(): it clears `submitted` only after form.reset().
    const queueSyncErrorState = () => {
      if (this.errorStateQueued) {
        return;
      }

      this.errorStateQueued = true;
      queueMicrotask(() => {
        this.errorStateQueued = false;
        if (!this.destroyed) {
          this.applyErrorState();
        }
      });
    };

    this.control.valueChanges?.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    this.control.statusChanges?.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    this.formGroupDirective?.ngSubmit.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    this.formValidationHelper.validationAttempted$
      .pipe(untilDestroyed(this))
      .subscribe((validatedForm) => {
        if (!this.belongsToValidatedForm(validatedForm)) {
          return;
        }

        this.showErrorsAfterValidationAttempt = true;
        this.applyErrorState();
      });

    this.control.valueChanges?.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.control.valid) {
        this.showErrorsAfterValidationAttempt = false;
      }
    });

    this.syncErrorState = queueSyncErrorState;
    queueSyncErrorState();
  }

  ngOnChanges(): void {
    if (this.validationAttempted) {
      this.applyErrorState();
      return;
    }
    this.syncErrorState?.();
  }

  private applyErrorState(): void {
    const controlErrors = this.control.errors;
    const submitted = !!this.formGroupDirective?.submitted;
    const shouldShow =
      !!controlErrors &&
      (this.showErrorsAfterValidationAttempt ||
        !!this.control.dirty ||
        submitted ||
        this.validationAttempted);

    if (shouldShow) {
      const firstKey = Object.keys(controlErrors)[0];
      const getError = this.errors[firstKey];
      const text = getError ? getError(controlErrors[firstKey]) : firstKey;
      this.formInvalid(text);
    } else {
      this.formValid();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  private belongsToValidatedForm(validatedForm: FormGroup | FormArray): boolean {
    const hostForm = this.formGroupDirective?.control;
    if (hostForm) {
      return hostForm === validatedForm;
    }

    return FormValidationHelperService.belongsToFormRoot(this.control.control, validatedForm);
  }

  private placeErrorOutsideInputGroup(): void {
    placeFormErrorElement(
      this.compRef?.location?.nativeElement as HTMLElement | undefined,
      this.elementRef.nativeElement as HTMLElement,
      {appendedInput: this.appendedInput},
    );
  }

  private formInvalid(text: any) {
    this.errorStatusChanged.emit(text ?? null);
    this.setErrorStyleForInput();
    if (text) {
      this.setError(text);
    } else {
      this.removeError();
    }
  }

  private formValid() {
    this.errorStatusChanged.emit(null);
    this.removeErrorStyleForInput();
    this.removeError();
  }
}
